import { TW_OVERFLOW_ELLIPSIS } from '../../../../universal';
import { React, _ } from '../../../lib';
import { LoadingSpinnerSmall } from '../loading';
import { CJSX, useRSInstance } from '../meta-types';
import { SidebarListGrid } from '../sidebar-listgrid';
import { confirmToggleThreadResolved, copyMessageToClipboard, handlePaneClick, handlePaneMouseDownCapture, handleTextboxChange, handleTextboxKeyDown, loadThreads, openCreateThreadForm, renameSelectedThread, sendMessage, updateSelectedThread, updateSidebarView, } from './actions';
import { ChatMessage } from './chat-message';
import { ChatComposer } from './composer';
import { formatFullDateTime, formatSidebarTimestamp } from './date-utils';
import { getThreadDisplayTitle } from './message-utils';
import { getChatCSSVars } from './theme';
import { ChatToolbar } from './toolbar';
import { clearCopyTickTimer, getContextScopeKey, getDefaultMessageIsVisibleToTarget, getDefaultState, getIsPrivilegedView, updateViewportAnchor, } from './utils';
export const ChatBasic = (props) => {
    const rs = useRSInstance({
        props,
        defaultState: getDefaultState,
        actionToDelta: null,
        refs: {
            messagesViewport: React.useRef(null),
            composerWrapper: React.useRef(null),
            textarea: React.useRef(null),
            isViewportAnchored: React.useRef(true),
            prevThreadKey: React.useRef(null),
            prevSelectedThreadMessageCount: React.useRef(0),
            shouldNudgeScrollToBottom: React.useRef(false),
            copyTickTimer: React.useRef(null),
            loadThreadsRequestKey: React.useRef(0),
        },
    });
    const selectedThread = _.find(rs.state.threads, thread => thread.ID === rs.state.selectedThreadID);
    const isPrivilegedView = getIsPrivilegedView(props);
    const contextScopeKey = getContextScopeKey(props);
    const resolveMessageIsVisibleToTarget = props.resolveMessageIsVisibleToTarget;
    const canSendMessage = selectedThread != null &&
        rs.state.messageText.trim() !== '' &&
        !rs.state.sending &&
        !rs.state.loading;
    const selectedThreadMessages = React.useMemo(() => _.chain(rs.state.messages)
        .filter(x => x.Thread === rs.state.selectedThreadID)
        .filter(x => isPrivilegedView
        ? true
        : (resolveMessageIsVisibleToTarget?.(x) ??
            getDefaultMessageIsVisibleToTarget(x)))
        .sortBy(x => x.CreatedAt)
        .value(), [
        isPrivilegedView,
        resolveMessageIsVisibleToTarget,
        rs.state.messages,
        rs.state.selectedThreadID,
    ]);
    const threadUpdatedAtByID = React.useMemo(() => _.chain(rs.state.messages)
        .groupBy(message => message.Thread)
        .mapValues(threadMessages => _.chain(threadMessages).sortBy('createdAt').last().value()
        ?.CreatedAt ?? null)
        .value(), [rs.state.messages]);
    const getThreadUpdatedAt = (thread) => threadUpdatedAtByID[thread.ID] ?? thread.CreatedAt;
    const onSendMessage = () => {
        sendMessage(rs);
    };
    const onPaneClick = (e) => {
        handlePaneClick(rs, e);
    };
    const onPaneMouseDownCapture = (e) => {
        handlePaneMouseDownCapture(rs, e);
    };
    const onTextboxChange = (e) => {
        handleTextboxChange(rs, e);
    };
    const onTextboxKeyDown = (e) => {
        handleTextboxKeyDown(rs, e);
    };
    const onCopyMessage = (message) => {
        copyMessageToClipboard(rs, message);
    };
    const canMutateSelectedThread = selectedThread != null && !props.isReadOnly && !rs.state.loading;
    const selectedThreadTitle = getThreadDisplayTitle(selectedThread);
    const toggleThreadActionLabel = selectedThread?.ResolvedAt == null ? 'Close thread' : 'Reopen thread';
    const toggleThreadButtonLabel = selectedThread?.ResolvedAt == null ? 'Close' : 'Reopen';
    const toggleThreadActionIcon = '/static/img/i8/ios16-swap.svg';
    const onRenameSelectedThread = () => {
        renameSelectedThread(rs);
    };
    const onConfirmToggleThreadResolved = () => {
        confirmToggleThreadResolved(rs);
    };
    React.useEffect(() => {
        clearCopyTickTimer(rs);
        rs.updateState(s => ({
            ...getDefaultState(),
            composerHeight: s.composerHeight,
        }));
        loadThreads(rs);
    }, [contextScopeKey, props.facility, isPrivilegedView]);
    React.useEffect(() => {
        const composerWrapper = rs.refs.composerWrapper.current;
        let observer = null;
        if (composerWrapper != null && window.ResizeObserver != null) {
            observer = new ResizeObserver(entries => {
                const nextHeight = entries[0]?.contentRect.height ?? 0;
                rs.updateState(s => s.composerHeight === nextHeight ? {} : { composerHeight: nextHeight });
            });
            observer.observe(composerWrapper);
            const initialComposerHeight = composerWrapper.getBoundingClientRect().height;
            rs.updateState(s => s.composerHeight === initialComposerHeight
                ? {}
                : { composerHeight: initialComposerHeight });
        }
        return () => {
            observer?.disconnect();
            clearCopyTickTimer(rs);
        };
    }, []);
    React.useEffect(() => {
        const viewport = rs.refs.messagesViewport.current;
        if (viewport == null) {
            return;
        }
        const threadKey = `${rs.state.selectedThreadID ?? 'none'}`;
        const selectedMessageCount = selectedThreadMessages.length;
        const didThreadChange = rs.refs.prevThreadKey.current !== threadKey;
        const didReceiveNewMessage = selectedMessageCount > rs.refs.prevSelectedThreadMessageCount.current;
        const shouldNudgeScrollToBottom = rs.refs.shouldNudgeScrollToBottom.current;
        if (didThreadChange ||
            shouldNudgeScrollToBottom ||
            (didReceiveNewMessage && rs.refs.isViewportAnchored.current)) {
            window.requestAnimationFrame(() => {
                const latestViewport = rs.refs.messagesViewport.current;
                if (latestViewport == null) {
                    return;
                }
                latestViewport.scrollTop = latestViewport.scrollHeight;
                rs.refs.isViewportAnchored.current = true;
                rs.refs.shouldNudgeScrollToBottom.current = false;
            });
        }
        rs.refs.prevThreadKey.current = threadKey;
        rs.refs.prevSelectedThreadMessageCount.current = selectedMessageCount;
    }, [selectedThreadMessages.length, rs.state.selectedThreadID]);
    const hasSelectedThread = rs.state.selectedThreadID != null &&
        _.some(rs.state.threads, thread => thread.ID === rs.state.selectedThreadID);
    const renderThreadPane = (selectedValue) => {
        const isSelectedThreadSettled = !rs.state.loading &&
            hasSelectedThread &&
            selectedValue === rs.state.selectedThreadID;
        return (React.createElement("div", { className: "tailwind-wrapper", style: { height: '100%' } },
            React.createElement("div", { className: "relative flex h-full flex-col bg-[oklch(var(--chat-l-surface)_0_0)]", style: {
                    ...getChatCSSVars(),
                    fontFamily: 'var(--font-condensed)',
                } },
                React.createElement("div", { className: "relative flex min-h-0 flex-1 flex-col" },
                    React.createElement("div", { className: "pointer-events-none absolute inset-0 z-0 bg-[oklch(var(--chat-l-text)_0_0_/_0.06)]" }),
                    React.createElement("div", { className: "pointer-events-none absolute inset-0 z-0", style: {
                            backgroundImage: 'url(/static/img/svg/window-background-texture.svg)',
                            backgroundRepeat: 'repeat',
                            backgroundSize: '5px',
                            filter: 'grayscale(45%)',
                            opacity: 0.16,
                        } }),
                    React.createElement(CJSX, { cond: !props.hideThreadToolbar },
                        React.createElement("div", { className: "relative z-20" },
                            React.createElement(ChatToolbar, { selectedThreadTitle: selectedThreadTitle, canMutateSelectedThread: canMutateSelectedThread, renameSelectedThread: onRenameSelectedThread, toggleThreadButtonLabel: toggleThreadButtonLabel, toggleThreadActionLabel: toggleThreadActionLabel, toggleThreadActionIcon: toggleThreadActionIcon, confirmToggleThreadResolved: onConfirmToggleThreadResolved }))),
                    React.createElement("div", { ref: rs.refs.messagesViewport, className: "relative z-10 flex min-h-0 flex-1 flex-col overflow-y-scroll px-1", onScroll: () => {
                            updateViewportAnchor(rs);
                        }, onMouseDownCapture: onPaneMouseDownCapture, onClick: onPaneClick },
                        React.createElement("div", { className: "mx-auto w-full max-w-[800px] pt-4", style: {
                                paddingBottom: `${Math.max(rs.state.composerHeight + 8, 48)}px`,
                            } },
                            React.createElement(CJSX, { cond: rs.state.loading },
                                React.createElement("div", { className: "mb-3 flex justify-center" },
                                    React.createElement(LoadingSpinnerSmall, null))),
                            React.createElement(CJSX, { cond: isSelectedThreadSettled &&
                                    selectedThreadMessages.length === 0 },
                                React.createElement("div", { className: "pt-12 text-center text-sm text-[oklch(var(--chat-l-text-muted)_0_0)]" }, "No messages yet in this thread")),
                            React.createElement(CJSX, { cond: !(isSelectedThreadSettled &&
                                    selectedThreadMessages.length === 0) },
                                React.createElement("div", { className: "space-y-4" }, _.map(selectedThreadMessages, message => (React.createElement(ChatMessage, { key: message.ID, rs: rs, message: message, onCopyMessage: onCopyMessage })))))),
                        React.createElement(ChatComposer, { rs: rs, canSendMessage: canSendMessage, onSendMessage: onSendMessage, onPaneClick: onPaneClick, onPaneMouseDownCapture: onPaneMouseDownCapture, onTextboxChange: onTextboxChange, onTextboxKeyDown: onTextboxKeyDown }))))));
    };
    if (props.hideThreadList) {
        return (React.createElement("div", { className: "h-full", style: getChatCSSVars() }, renderThreadPane(rs.state.selectedThreadID)));
    }
    return (React.createElement("div", { className: "h-full", style: getChatCSSVars() },
        React.createElement(SidebarListGrid, { data: rs.state.threads, pk: thread => thread.ID, inactivity: {
                lbl: {
                    text: 'Resolved',
                    width: 80,
                },
                isInactive: thread => thread.ResolvedAt != null,
            }, multiple: false, value: rs.state.selectedThreadID, onUpdateValue: threadID => {
                updateSelectedThread(rs, threadID);
            }, view: rs.state.sidebarView, onUpdateView: sidebarView => {
                updateSidebarView(rs, sidebarView);
            }, adding: props.isReadOnly
                ? null
                : {
                    lbl: {
                        text: '',
                        width: 28,
                    },
                    onClick: () => {
                        openCreateThreadForm(rs);
                    },
                }, sidebarWidth: { defaultWidth: 240, minWidth: 200, maxWidth: 400 }, listGridSettings: {
                defaultSortingKey: 'Updated',
                defaultSortingReverse: true,
            }, fields: [
                {
                    key: 'Title',
                    lbl: 'Title',
                    className: () => TW_OVERFLOW_ELLIPSIS,
                    text: thread => getThreadDisplayTitle(thread),
                    sortVal: thread => getThreadDisplayTitle(thread),
                    tooltip: thread => getThreadDisplayTitle(thread),
                },
                {
                    key: 'Updated',
                    lbl: 'Updated',
                    className: () => `text-right ${TW_OVERFLOW_ELLIPSIS}`,
                    width: { init: 88, flex: 0.55 },
                    text: thread => formatSidebarTimestamp(getThreadUpdatedAt(thread)),
                    sortVal: thread => getThreadUpdatedAt(thread),
                    tooltip: thread => formatFullDateTime(getThreadUpdatedAt(thread)),
                },
            ], stubMessage: { lbl: 'Select a thread or click Add to start one' }, buildForm: renderThreadPane })));
};
//# sourceMappingURL=main.js.map